import { computed, ReadonlySignal, Signal } from "@preact/signals-core";
import { MechanicsCalculator } from "../shared/MechanicsCalculator";
import { GameState, PlayerStatistics, UxState, type ILocalPlayer } from "./GameState";
import { ServerOpCode } from "../shared/OpCodes";
import type { IServerMessage } from "../shared/IServerMessage";
import type { IGameData } from "../shared/IGameData";
import type { ISharedPlayer } from "../shared/IPlayer";
import { deepEqual } from "../Utils";
import { Notification } from "../ux/hud/Notification.ts";
import { getScm } from './Globals';
import { IServerNotification, NotificationType } from "../shared/IServerNotification.ts";

import { createLogger } from "../shared/Logger";
const logger = createLogger("AppState");

abstract class AbstractStore<T, A> {
    protected state: Signal<T>;

    constructor(private reducer: (action: A, state: T) => T, initialState?: T) {
        this.state = new Signal(initialState as T);
    }
    public dispatch = (action: A): void => {
        const prev = this.state.value;
        const next = this.reducer(action, prev);
        if (next === prev) {
            // fast path
            logger.log("dispatch: state unchanged (equal), skipping...");
            return;
        }
        if (deepEqual(prev, next)) {
           
            logger.log("dispatch: state unchanged (deep equal), skipping...");
            return;
        }
        this.state.value = next;    // only updates when something truly changed
    };

    public get current() {
        return this.state.value;
    }

    public select<R>(selector: (state: T) => R): ReadonlySignal<R> {
        let lastValue = selector(this.state.value);
        return computed(() => {
            const nextValue = selector(this.state.value);
            if (!deepEqual(lastValue, nextValue)) {
                lastValue = nextValue;
            }
            return lastValue;
        });
    }

    // Overload signatures for react method
    public react<R>(selector: (state: T) => R, effect: (state: R) => void): () => void;
    public react<R>(selectors: Array<(state: T) => R>, effect: (state: R[]) => void): () => void;

    // Implementation of react method
    public react<R>(selectorOrSelectors: ((state: T) => R) | Array<(state: T) => R>, effect: (state: R | R[]) => void): () => void {
        if (Array.isArray(selectorOrSelectors)) {
            const computed = this.selectMultiple(selectorOrSelectors);
            const dispose = computed.subscribe(effect as (state: R[]) => void);
            return dispose;
        } else {
            const computed = this.select(selectorOrSelectors);
            const dispose = computed.subscribe(effect as (state: R) => void);
            return dispose;
        }
    }

    private selectMultiple<R>(selectors: Array<(state: T) => R>): ReadonlySignal<R[]> {
        let lastValues = selectors.map(selector => selector(this.state.value));
        return computed(() => {
            const nextValues = selectors.map(selector => selector(this.state.value));
            if (!deepEqual(lastValues, nextValues)) {
                lastValues = nextValues;
            }
            return lastValues;
        });
    }
}
/**
 * Simple helper to retain previous value if equal to new one.
 * @param prev 
 * @param next 
 * @param eq 
 * @returns 
 */
const retainIfEqual = <T>(prev: T, next: T, eq = deepEqual): T =>
    eq(prev, next) ? prev : next;

/**
 * Reducer function for updating game state based on server messages.
 * @param msg Server message
 * @param state Current game state
 * @returns New game state
 */
const reducer = (msg: IServerMessage, state: GameState): GameState => {
    let newState: GameState;
    let sharedPlayer!: ISharedPlayer;

    switch (msg.Code) {
        case ServerOpCode.FormatError:
        case ServerOpCode.StateError:
            //  TODO: propagate error messages to UI in a generic way
            logger.error("Server error:", msg.Data);
            const stateNotification = new Notification({ title: "State error", message: JSON.stringify(msg.Data), type: NotificationType.Error });
            getScm().MasterHudOverlay.addChild(stateNotification);
            return state;

        case ServerOpCode.PlayerState: {
            const p = JSON.parse(msg.Data) as ILocalPlayer;
            const localPlayer = retainIfEqual(state.localPlayer, p);
            const emails = retainIfEqual(state.emails, p.emails);

            // nothing changed → keep whole state reference
            if (localPlayer === state.localPlayer && emails === state.emails) return state;

            newState = {
                ...state,
                localPlayer: localPlayer,
                emails: emails,
            };

            //  pre-calculate statistics if local player data has changed
            if (localPlayer !== state.localPlayer) {
                updateStatistics(newState);
            }
            return newState;
        }

        case ServerOpCode.GameData:
            const data = JSON.parse(msg.Data) as IGameData;
            newState = {
                ...state,
                players: new Map(data.players.map(player => [player.id, player])),
                configuration: data.configuration,
                worldMap: data.worldMap,
                troopDefinitions: data.troopDefinitions,
                itemDefinitions: data.dropItemsDefinitions,
                mechanicsCalculator: new MechanicsCalculator(data.configuration, data.worldMap.locations, data.troopDefinitions, data.dropItemsDefinitions),
            };
            return newState;

        case ServerOpCode.PlayerCreated:
        case ServerOpCode.PlayerNameChanged: {
            sharedPlayer = JSON.parse(msg.Data) as ISharedPlayer;
            const prev = state.players.get(sharedPlayer.id);
            if (prev && deepEqual(prev, sharedPlayer)) return state;

            const players = new Map(state.players);
            players.set(sharedPlayer.id, sharedPlayer);
            newState = { ...state, players };
            return newState;
        }

        case ServerOpCode.ServerNotification: //  TODO: remove OpCode and replace with generic notification sending mechanism
            const payload = JSON.parse(msg.Data) as IServerNotification;
            const notification = new Notification(payload);
            getScm().MasterHudOverlay.addChild(notification);
            return state;

        case ServerOpCode.Error:
            const errorNotification = new Notification({ title: "Error", message: JSON.stringify(msg.Data), type: NotificationType.Error });
            getScm().MasterHudOverlay.addChild(errorNotification);
            return state;

        default:
            logger.warn("Unknown server message:", JSON.stringify(msg));
            return state;
    }
};

/**
 * Update player statistics. 
 * Note: this function mutates the state!
 * @param state Game state
 */
const updateStatistics = (state: GameState) => {
    if (!state.localPlayer || !state.mechanicsCalculator) {
        return;
    }

    //  calculate production and consumption per minute
    let stats = new PlayerStatistics();
    const { housingCapacity } = state.mechanicsCalculator.calcHousings(state.localPlayer);
    const { productionPerMinute, consumptionPerMinute, totalPopulation, totalUnits } = state.mechanicsCalculator.calcResourcesProductionPerMinute(state.localPlayer);
    stats.productionPerMinute = productionPerMinute;
    stats.consumptionPerMinute = consumptionPerMinute;
    stats.population = totalPopulation;
    stats.units = totalUnits;
    stats.housingCapacity = housingCapacity;
    stats.maxStorageCapacity = state.mechanicsCalculator.calcMaxStorageCapacity(state.localPlayer);
    state.localPlayer.statistics = stats;
}

class AppState extends AbstractStore<GameState, IServerMessage> {
    constructor() {
        super(reducer, new GameState());
    }

    public get uxState() {
        return this.state.value.uxState;
    }

    public set uxState(value: UxState) {
        const oldState = this.state.value;
        const newState = { ...oldState, uxState: value };
        this.state.value = newState;
    }

    public set selectedHeroId(id: number | undefined) {
        const oldState = this.state.value;
        const newState = { ...oldState, uxState: { ...oldState.uxState, selectedHeroId: id } };
        this.state.value = newState;
    }
}

/**
 * Global application state store singleton.
 */
export const appState = new AppState();
